import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

const monoFont = "'IBM Plex Mono', monospace";

interface Props {
  onClose: () => void;
  onCreated: () => void;
}

function generateCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 8; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

export function CouponModal({ onClose, onCreated }: Props) {
  const [code, setCode] = useState(generateCode());
  const [description, setDescription] = useState('');
  const [discountType, setDiscountType] = useState<'months_free' | 'percentage'>('months_free');
  const [discountValue, setDiscountValue] = useState(1);
  const [plans, setPlans] = useState<string[]>(['free', 'pro', 'custom']);
  const [maxUses, setMaxUses] = useState<number | null>(null);
  const [unlimited, setUnlimited] = useState(true);
  const [expiresAt, setExpiresAt] = useState('');
  const [saving, setSaving] = useState(false);

  const togglePlan = (p: string) => {
    setPlans(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  };

  const handleCreate = async () => {
    if (!code.trim()) return;
    setSaving(true);
    const { error } = await supabase.from('coupons').insert({
      code: code.trim().toUpperCase(),
      description: description.trim() || null,
      discount_type: discountType,
      discount_value: discountValue,
      applicable_plans: plans,
      max_uses: unlimited ? null : maxUses,
      expires_at: expiresAt || null,
    } as any);

    if (error) {
      toast({ title: 'Erro', description: error.message.includes('duplicate') ? 'Código já existe' : 'Erro ao criar cupom', variant: 'destructive' });
      setSaving(false);
      return;
    }
    toast({ title: 'Cupom criado' });
    onCreated();
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
    letterSpacing: '0.05em', color: 'rgba(247,246,242,0.45)', marginBottom: 6, display: 'block',
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: monoFont, fontSize: 14, fontWeight: 300, width: '100%',
    padding: '10px 12px', border: '0.5px solid rgba(247,246,242,0.12)',
    backgroundColor: 'rgba(247,246,242,0.04)', color: 'rgba(247,246,242,0.85)',
  };

  const chipStyle = (active: boolean): React.CSSProperties => ({
    fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
    padding: '5px 12px', border: `0.5px solid ${active ? 'rgba(247,246,242,0.4)' : 'rgba(247,246,242,0.12)'}`,
    backgroundColor: active ? 'rgba(247,246,242,0.08)' : 'transparent',
    color: active ? '#F7F6F2' : 'rgba(247,246,242,0.4)', cursor: 'pointer',
    transition: 'all 0.15s ease',
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.7)',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        backgroundColor: '#0A0A0A', border: '0.5px solid rgba(247,246,242,0.12)',
        padding: 24, maxWidth: 420, width: '100%', maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(247,246,242,0.4)', marginBottom: 20 }}>
          NOVO CUPOM
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>Código</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={code} onChange={e => setCode(e.target.value.toUpperCase())} style={{ ...inputStyle, fontFamily: monoFont, fontSize: 16, letterSpacing: '0.1em' }} />
              <button onClick={() => setCode(generateCode())} style={{
                fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
                padding: '0 12px', border: '0.5px solid rgba(247,246,242,0.12)', backgroundColor: 'transparent',
                color: 'rgba(247,246,242,0.5)', cursor: 'pointer', whiteSpace: 'nowrap',
              }}>GERAR</button>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Descrição (opcional)</label>
            <input value={description} onChange={e => setDescription(e.target.value)} style={inputStyle} placeholder="Nota interna..." />
          </div>

          <div>
            <label style={labelStyle}>Tipo de desconto</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setDiscountType('months_free')} style={chipStyle(discountType === 'months_free')}>
                Meses grátis
              </button>
              <button onClick={() => setDiscountType('percentage')} style={chipStyle(discountType === 'percentage')}>
                Desconto %
              </button>
            </div>
          </div>

          <div>
            <label style={labelStyle}>{discountType === 'months_free' ? 'Meses (1–12)' : 'Percentual (1–100)'}</label>
            <input
              type="number"
              min={1} max={discountType === 'months_free' ? 12 : 100}
              value={discountValue}
              onChange={e => setDiscountValue(Math.max(1, Math.min(discountType === 'months_free' ? 12 : 100, parseInt(e.target.value) || 1)))}
              style={{ ...inputStyle, width: 100 }}
            />
          </div>

          <div>
            <label style={labelStyle}>Planos aplicáveis</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['free', 'pro', 'custom'].map(p => (
                <button key={p} onClick={() => togglePlan(p)} style={chipStyle(plans.includes(p))}>
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={labelStyle}>Máximo de usos</label>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <label style={{ fontFamily: monoFont, fontSize: 12, color: 'rgba(247,246,242,0.6)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={unlimited} onChange={e => setUnlimited(e.target.checked)} />
                Ilimitado
              </label>
              {!unlimited && (
                <input type="number" min={1} value={maxUses || ''} onChange={e => setMaxUses(parseInt(e.target.value) || 1)} style={{ ...inputStyle, width: 80 }} />
              )}
            </div>
          </div>

          <div>
            <label style={labelStyle}>Expira em (opcional)</label>
            <input type="date" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} style={inputStyle} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
          <button onClick={onClose} style={{
            fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
            padding: '8px 16px', border: '0.5px solid rgba(247,246,242,0.12)', backgroundColor: 'transparent',
            color: 'rgba(247,246,242,0.5)', cursor: 'pointer',
          }}>CANCELAR</button>
          <button onClick={handleCreate} disabled={saving || !code.trim()} style={{
            fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
            padding: '8px 16px', border: '0.5px solid #00E87A', backgroundColor: 'rgba(0,232,122,0.1)',
            color: '#00E87A', cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.5 : 1,
          }}>
            {saving ? 'CRIANDO...' : 'CRIAR CUPOM'}
          </button>
        </div>
      </div>
    </div>
  );
}
